/**
 * Module Registry
 * 
 * Central registry for all analysis modules.
 * Each module defines its configuration, edge function, and processing logic.
 */

export interface ModuleConfig {
  id: string;
  name: string;
  description: string;
  edgeFunctionName: string;
  defaultTolerance?: number;
  supportedFileTypes: string[];
  requiresFieldMapping: boolean;
}

/**
 * Registry of all available analysis modules
 */
export const MODULE_REGISTRY: Record<string, ModuleConfig> = {
  taxes_fees: {
    id: 'taxes_fees',
    name: 'Skattekontoanalys',
    description: 'Automatisk AI-analys av skattekontouttag från Skatteverket',
    edgeFunctionName: 'audit-tax-account-analyze',
    defaultTolerance: 3,
    supportedFileTypes: ['csv', 'txt'],
    requiresFieldMapping: true,
  },
  revenue: {
    id: 'revenue',
    name: 'Intäktsanalys',
    description: 'Automatisk AI-analys av intäktsunderlag och fakturajournaler',
    edgeFunctionName: 'audit-revenue-analyze',
    defaultTolerance: 5,
    supportedFileTypes: ['csv', 'xlsx'],
    requiresFieldMapping: true,
  },
  // Future modules will be added here, e.g.:
  // annual_report: {
  //   id: 'annual_report',
  //   name: 'Årsbokslut',
  //   description: 'AI-analys av årsbokslut',
  //   edgeFunctionName: 'audit-annual-report-analyze',
  //   supportedFileTypes: ['pdf', 'xlsx'],
  //   requiresFieldMapping: false,
  // },
};

/**
 * Get module configuration by module type
 */
export function getModuleConfig(moduleType: string): ModuleConfig {
  const config = MODULE_REGISTRY[moduleType];
  if (!config) {
    throw new Error(`Unknown module type: ${moduleType}`);
  }
  return config;
}

/**
 * Get edge function name for a module type
 */
export function getEdgeFunctionForModule(moduleType: string): string {
  return getModuleConfig(moduleType).edgeFunctionName;
}

/**
 * Check if a module exists in the registry
 */
export function isValidModuleType(moduleType: string): boolean {
  return moduleType in MODULE_REGISTRY;
}

/**
 * Get all registered module types
 */
export function getAllModuleTypes(): string[] {
  return Object.keys(MODULE_REGISTRY);
}
